import { useState } from 'react';
import type { ReactElement } from 'react';

import {
  AlertCircle,
  Building2,
  CalendarDays,
  Check,
  FilePlus2,
  Loader2,
  Pencil,
  Plus,
  ShieldCheck,
  Users,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

import type { MortgageRequiredDocumentSpec } from '../api/mortgageEntities.api';

interface Props {
  /** null opens the dialog to add a new document. */
  spec: MortgageRequiredDocumentSpec | null;
  /** The card's employment type, checked by default on a new document. An edited one keeps its own. */
  employmentType: string;
  employmentTypeOptions: string[];
  baseDocumentTypes: Record<string, string[]>;
  entityName: string;
  takenDocumentTypes: string[];
  isSaving: boolean;
  saveError: string | null;
  onCancel: () => void;
  onSave: (spec: MortgageRequiredDocumentSpec) => void;
}

type RequestedFor = NonNullable<MortgageRequiredDocumentSpec['requestedFor']>;

interface Draft {
  documentType: string;
  quantity: string;
  isMonthly: boolean;
  validityDays: string;
  requestedFor: RequestedFor;
  employmentTypes: string[];
  isMinimumRequired: boolean;
}

const REQUESTED_FOR_OPTIONS: { value: RequestedFor; label: string }[] = [
  { value: 'all', label: 'Titular y complemento' },
  { value: 'mainBuyer', label: 'Solo titular' },
  { value: 'complements', label: 'Solo complemento' },
];

const isRequestedFor = (value: string): value is RequestedFor =>
  REQUESTED_FOR_OPTIONS.some((option) => option.value === value);

const formatEmploymentType = (value: string): string => value.replace(/_/g, ' ');

const toDraft = (
  spec: MortgageRequiredDocumentSpec | null,
  employmentType: string,
  employmentTypeOptions: string[]
): Draft => ({
  documentType: spec?.documentType ?? '',
  quantity: String(spec?.quantity ?? 1),
  isMonthly: spec?.periodGranularity === 'month',
  validityDays: spec?.validityDays ? String(spec.validityDays) : '',
  requestedFor: spec?.requestedFor ?? 'all',
  employmentTypes: spec ? spec.employmentTypes ?? employmentTypeOptions : [employmentType],
  isMinimumRequired: spec?.isMinimumRequired === true,
});

const isPositiveInteger = (raw: string): boolean => /^[1-9]\d*$/.test(raw.trim());

const isSameName = (left: string, right: string): boolean => left.trim().toLowerCase() === right.trim().toLowerCase();

const validate = (
  draft: Draft,
  takenDocumentTypes: string[],
  baseDocumentTypes: Record<string, string[]>
): string | null => {
  const name = draft.documentType.trim();
  if (!name) return 'El documento necesita un nombre.';
  if (takenDocumentTypes.some((taken) => isSameName(taken, name))) {
    return 'Ya existe un documento con ese nombre en esta entidad.';
  }
  if (draft.employmentTypes.length === 0) return 'Elige al menos un tipo de empleo.';
  const clashingTypes = draft.employmentTypes.filter((type) =>
    (baseDocumentTypes[type] ?? []).some((base) => isSameName(base, name))
  );
  if (clashingTypes.length > 0) {
    return `"${name}" ya es un documento base del país para ${clashingTypes.map(formatEmploymentType).join(', ')}.`;
  }
  if (!isPositiveInteger(draft.quantity)) return 'La cantidad tiene que ser un entero mayor a 0.';
  if (draft.validityDays.trim() && !isPositiveInteger(draft.validityDays)) {
    return 'La vigencia tiene que ser un entero mayor a 0, o quedar vacía.';
  }
  return null;
};

/* Fields left at their default are not stored: no employmentTypes or requestedFor means every client. */
const toSpec = (draft: Draft, employmentTypeOptions: string[]): MortgageRequiredDocumentSpec => {
  const coversEveryType = employmentTypeOptions.every((type) => draft.employmentTypes.includes(type));
  return {
    documentType: draft.documentType.trim(),
    quantity: Number(draft.quantity),
    ...(draft.isMonthly && { periodGranularity: 'month' as const }),
    ...(draft.validityDays.trim() && { validityDays: Number(draft.validityDays) }),
    ...(!coversEveryType && {
      employmentTypes: employmentTypeOptions.filter((type) => draft.employmentTypes.includes(type)),
    }),
    ...(draft.requestedFor !== 'all' && { requestedFor: draft.requestedFor }),
    ...(draft.isMinimumRequired && { isMinimumRequired: true }),
  };
};

const MortgageAdditionalDocumentDialog = ({
  spec,
  employmentType,
  employmentTypeOptions,
  baseDocumentTypes,
  entityName,
  takenDocumentTypes,
  isSaving,
  saveError,
  onCancel,
  onSave,
}: Props): ReactElement => {
  const [draft, setDraft] = useState<Draft>(() => toDraft(spec, employmentType, employmentTypeOptions));
  const [error, setError] = useState<string | null>(null);

  const handleSave = (): void => {
    const validationError = validate(draft, takenDocumentTypes, baseDocumentTypes);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    onSave(toSpec(draft, employmentTypeOptions));
  };

  const toggleEmploymentType = (type: string, checked: boolean): void => {
    setDraft((current) => ({
      ...current,
      employmentTypes: checked
        ? [...current.employmentTypes, type]
        : current.employmentTypes.filter((item) => item !== type),
    }));
  };

  const shownError = error ?? saveError;
  const coversEveryType = employmentTypeOptions.every((type) => draft.employmentTypes.includes(type));
  const hasRequiredFields = draft.documentType.trim().length > 0 && isPositiveInteger(draft.quantity);

  return (
    <Dialog open onOpenChange={(open) => !open && !isSaving && onCancel()}>
      <DialogContent
        className="flex max-h-[90dvh] flex-col gap-0 overflow-hidden bg-bg-surface p-0 shadow-lg sm:max-w-lg"
        aria-busy={isSaving}
      >
        <DialogHeader className="gap-4 border-b border-border-subtle bg-brand-vivid/4 px-5 py-5 sm:px-6 sm:py-6">
          <div className="flex items-center gap-3 pr-6">
            <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-brand-vivid/10 text-brand-vivid">
              {spec ? <Pencil className="size-5" /> : <FilePlus2 className="size-5" />}
            </div>
            <div className="space-y-1.5">
              <p className="m-0 text-xs font-semibold uppercase tracking-wider text-brand-vivid">Documento adicional</p>
              <DialogTitle className="text-xl font-semibold leading-tight text-brand-navy">
                {spec ? 'Editar documento' : 'Agregar documento'}
              </DialogTitle>
            </div>
          </div>
          <DialogDescription className="text-sm leading-relaxed text-text-muted">
            Define el documento y las condiciones para solicitarlo al cliente.
          </DialogDescription>
          <div className="flex flex-wrap items-center gap-2 text-xs font-medium">
            <span className="inline-flex min-w-0 items-center gap-1.5 rounded-md border border-brand-vivid/10 bg-bg-surface px-2.5 py-1 text-brand-navy">
              <Building2 className="size-3.5 shrink-0 text-brand-vivid" />
              <span className="break-words">{entityName}</span>
            </span>
            <span className="rounded-md bg-brand-vivid/8 px-2.5 py-1 text-brand-vivid capitalize">
              {coversEveryType
                ? 'Todos los tipos de empleo'
                : employmentTypeOptions
                    .filter((type) => draft.employmentTypes.includes(type))
                    .map(formatEmploymentType)
                    .join(', ') || 'Sin tipo de empleo'}
            </span>
          </div>
        </DialogHeader>

        <div className="flex min-h-0 flex-col gap-5 overflow-y-auto px-5 py-5 sm:px-6 sm:py-6">
          <div className="space-y-2">
            <Label htmlFor="additional-document-type" className="text-brand-navy">
              Nombre del documento
            </Label>
            <Input
              id="additional-document-type"
              className="h-10 bg-bg-surface focus-visible:border-border-focus focus-visible:ring-brand-vivid/15"
              placeholder="Ej. Certificado de antigüedad laboral"
              value={draft.documentType}
              onChange={(event) => setDraft((current) => ({ ...current, documentType: event.target.value }))}
              disabled={isSaving}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="additional-document-quantity" className="text-brand-navy">
                {draft.isMonthly ? 'Cantidad de meses' : 'Cantidad de archivos'}
              </Label>
              <Input
                id="additional-document-quantity"
                className="h-10 focus-visible:border-border-focus focus-visible:ring-brand-vivid/15"
                type="number"
                min={1}
                value={draft.quantity}
                onChange={(event) => setDraft((current) => ({ ...current, quantity: event.target.value }))}
                disabled={isSaving}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="additional-document-validity" className="text-brand-navy">
                Vigencia (días)
              </Label>
              <Input
                id="additional-document-validity"
                className="h-10 focus-visible:border-border-focus focus-visible:ring-brand-vivid/15"
                type="number"
                min={1}
                placeholder="Sin vigencia"
                value={draft.validityDays}
                onChange={(event) => setDraft((current) => ({ ...current, validityDays: event.target.value }))}
                disabled={isSaving}
                aria-describedby="additional-document-validity-hint"
              />
              <p id="additional-document-validity-hint" className="m-0 text-xs leading-relaxed text-text-subtle">
                Déjala vacía si el documento no vence.
              </p>
            </div>
          </div>

          <div
            className={cn(
              'flex items-center justify-between gap-4 rounded-xl border p-4 transition-colors',
              draft.isMonthly ? 'border-brand-vivid/25 bg-brand-vivid/5' : 'border-border-subtle bg-bg-surface-raised'
            )}
          >
            <div className="flex items-start gap-3">
              <CalendarDays
                className={cn('mt-0.5 size-4 shrink-0', draft.isMonthly ? 'text-brand-vivid' : 'text-text-subtle')}
              />
              <div className="space-y-1">
                <Label htmlFor="additional-document-monthly" className="text-brand-navy">
                  Un archivo por mes
                </Label>
                <p id="additional-document-monthly-hint" className="m-0 text-xs leading-relaxed text-text-muted">
                  Ideal para liquidaciones o boletas de pago.
                </p>
              </div>
            </div>
            <Switch
              id="additional-document-monthly"
              checked={draft.isMonthly}
              onCheckedChange={(checked) => setDraft((current) => ({ ...current, isMonthly: checked }))}
              disabled={isSaving}
              aria-describedby="additional-document-monthly-hint"
            />
          </div>

          <div
            className={cn(
              'flex items-center justify-between gap-4 rounded-xl border p-4 transition-colors',
              draft.isMinimumRequired
                ? 'border-brand-vivid/25 bg-brand-vivid/5'
                : 'border-border-subtle bg-bg-surface-raised'
            )}
          >
            <div className="flex items-start gap-3">
              <ShieldCheck
                className={cn(
                  'mt-0.5 size-4 shrink-0',
                  draft.isMinimumRequired ? 'text-brand-vivid' : 'text-text-subtle'
                )}
              />
              <div className="space-y-1">
                <Label htmlFor="additional-document-minimum" className="text-brand-navy">
                  Requerido antes de derivar
                </Label>
                <p id="additional-document-minimum-hint" className="m-0 text-xs leading-relaxed text-text-muted">
                  El cliente no se deriva a la entidad hasta entregarlo, igual que los documentos base.
                </p>
              </div>
            </div>
            <Switch
              id="additional-document-minimum"
              checked={draft.isMinimumRequired}
              onCheckedChange={(checked) => setDraft((current) => ({ ...current, isMinimumRequired: checked }))}
              disabled={isSaving}
              aria-describedby="additional-document-minimum-hint"
            />
          </div>

          <div className="border-t border-border-subtle pt-5">
            <fieldset className="min-w-0">
              <legend className="mb-2 text-sm font-medium text-brand-navy">¿A qué tipos de empleo se solicita?</legend>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {employmentTypeOptions.map((type) => (
                  <div key={type} className="flex items-center gap-2 rounded-md border border-border-subtle p-3">
                    <Checkbox
                      id={`additional-document-employment-${type}`}
                      checked={draft.employmentTypes.includes(type)}
                      onCheckedChange={(checked) => toggleEmploymentType(type, checked === true)}
                      disabled={isSaving}
                    />
                    <Label htmlFor={`additional-document-employment-${type}`} className="font-normal capitalize">
                      {formatEmploymentType(type)}
                    </Label>
                  </div>
                ))}
              </div>
            </fieldset>
          </div>

          <div className="space-y-2 border-t border-border-subtle pt-5">
            <Label htmlFor="additional-document-requested-for" className="text-brand-navy">
              ¿A quién se solicita?
            </Label>
            <Select
              value={draft.requestedFor}
              onValueChange={(value) => {
                if (isRequestedFor(value)) setDraft((current) => ({ ...current, requestedFor: value }));
              }}
              disabled={isSaving}
            >
              <SelectTrigger
                id="additional-document-requested-for"
                className="w-full focus-visible:border-border-focus focus-visible:ring-brand-vivid/15 data-[size=default]:h-10"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <Users className="size-4 shrink-0 text-text-subtle" />
                  <SelectValue />
                </span>
              </SelectTrigger>
              <SelectContent>
                {REQUESTED_FOR_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {shownError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-error/20 bg-error/5 p-3 text-sm text-error"
            >
              <AlertCircle className="mt-0.5 size-4 shrink-0" />
              <p className="m-0">{shownError}</p>
            </div>
          )}
        </div>

        <DialogFooter className="m-0 shrink-0 gap-2 border-border-subtle bg-bg-surface-raised px-5 py-4 sm:px-6">
          <Button type="button" variant="outline" className="h-10 px-4" onClick={onCancel} disabled={isSaving}>
            Cancelar
          </Button>
          <Button
            type="button"
            className="h-10 gap-2 px-4 shadow-sm disabled:bg-neutral-200 disabled:text-text-faint disabled:opacity-100 disabled:shadow-none"
            onClick={handleSave}
            disabled={isSaving || !hasRequiredFields}
          >
            {isSaving ? (
              <Loader2 className="size-4 animate-spin" />
            ) : spec ? (
              <Check className="size-4" />
            ) : (
              <Plus className="size-4" />
            )}
            {isSaving ? 'Guardando...' : spec ? 'Guardar cambios' : 'Agregar documento'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MortgageAdditionalDocumentDialog;
